import axios from '@axios'
import { storePagination, sorting } from '@/mixins'

const getInitialState = () => ({
  ...storePagination.state(),
  stockCountingForm: {
    id: null,
    status: null,
    store: null,
    group: null,
    subgroup: null,
    brand: null,
    department: null,
    description: null,
    percentCompleted: null,
    skusCount: null,
    deviationThresholdPercent: null,
    createdBy: null,
    createdDate: null,
    cancelationUser: null,
    cancelationDate: null,
    cancelationReason: null,
    completedUser: null,
    completedDate: null,
  },
  filters: {
    skuId: null,
  },
  stockCountingSkus: [],
  busy: false,
})

export default {
  namespaced: true,
  state: getInitialState(),

  getters: {},

  mutations: {
    ...storePagination.mutations,

    SET_STOCK_COUNTING(state, stockCounting) {
      state.stockCountingForm = stockCounting
    },
    SET_STOCK_COUNTING_SKUS(state, skus) {
      state.stockCountingSkus = skus
    },
    SET_FILTERS(state, filters) {
      state.filters = filters
    },
    CLEAN_FILTERS(state) {
      const { filters } = getInitialState()
      state.filters = filters
    },
    CLEAN_STATE(state) {
      const initialState = getInitialState()
      Object.keys(initialState).forEach(key => {
        state[key] = initialState[key]
      })
    },
    SET_BUSY(state, busy) {
      state.busy = busy
    },
  },

  actions: {
    ...storePagination.actions,

    async fetchCounting({ commit }, id) {
      const { data } = await axios.get(`/api/stock-countings/${id}`)
      commit('SET_STOCK_COUNTING', data)
    },

    async fetchCountingSkus({ commit, state }, stockCountingId) {
      const { data } = await axios.get(`/api/stock-counting-skus/${stockCountingId}`, {
        params: {
          skuId: state.filters.skuId,
          pageSize: state.paging.pageSize,
          pageIndex: state.paging.currentPage - 1,
          sortBy: sorting.methods.getSorting(state),
        },
      })

      commit('SET_STOCK_COUNTING_SKUS', data.results || [])
      commit('SET_PAGING', {
        ...state.paging,
        rowCount: data.rowCount,
        rowsInCurrentPage: data.rowsInCurrentPage,
      })
    },

    async generateReport({ state }, stockCountingId) {
      const { data } = await axios.get(`/api/stock-counting-skus/${stockCountingId}/download`, {
        params: {
          skuId: state.filters.skuId,
          pageSize: 0,
          pageIndex: 0,
          sortBy: sorting.methods.getSorting(state),
        },
        responseType: 'blob',
      })
      return data
    },

    async saveCounting({ state }) {
      const payload = {
        storeId: state.stockCountingForm.storeId,
        groupId: state.stockCountingForm.groupId,
        subgroupId: state.stockCountingForm.subgroupId,
        brandId: state.stockCountingForm.brandId,
        departmentId: state.stockCountingForm.departmentId,
        description: state.stockCountingForm.description,
      }
      const { data } = await axios.post('/api/stock-countings', payload)
      return data
    },

    async finishCounting({ state }) {
      const { data } = await axios.post(`/api/stock-countings/finish/${state.stockCountingForm.id}`)
      return data
    },

    async cancelCounting({ state }, reason) {
      const { data } = await axios.patch(
        `/api/stock-countings/cancel/${state.stockCountingForm.id}`,
        { reason }
      )
      return data
    },

    async saveSkuStockCounted({ state, commit }, { skuId, stockCounted }) {
      commit('SET_BUSY', true)
      try {
        const { data } = await axios.patch(
          `/api/stock-countings/${state.stockCountingForm.id}/${skuId}/stock-counted`,
          { stockCounted }
        )
        commit('SET_STOCK_COUNTING', {
          ...state.stockCountingForm,
          percentCompleted: data.countingPercentCompleted,
        })
        return data
      } finally {
        commit('SET_BUSY', false)
      }
    },

    async resetSkuStockCounted({ state, commit }, skuId) {
      const { data } = await axios.patch(
        `/api/stock-countings/${state.stockCountingForm.id}/${skuId}/stock-counted-reset`
      )
      commit('SET_STOCK_COUNTING', {
        ...state.stockCountingForm,
        percentCompleted: data.countingPercentCompleted,
      })
      return data
    },

    setFilters({ commit }, filters) {
      commit('SET_FILTERS', filters)
    },

    resetFilters({ commit }) {
      commit('CLEAN_FILTERS')
    },

    cleanState({ commit }) {
      commit('CLEAN_STATE')
    },
  },
}
